<template>
  <div class="user-favorites-container">
    <!-- Header -->
    <el-card shadow="never" class="header-card">
      <div class="header-content">
        <div class="left-section">
          <el-button icon="el-icon-back" plain @click="goBack">返回</el-button>
          <div class="user-info">
            <h3 class="page-title">用户收藏的名字列表</h3>
            <p class="user-meta" v-if="userInfo">
              <span>用户ID: {{ userInfo.id || userId }}</span>
              <span v-if="userInfo.nickname">昵称: {{ userInfo.nickname }}</span>
            </p>
          </div>
        </div>
        <div class="right-section">
          <el-tag :type="userInfo && userInfo.is_vip ? 'success' : 'info'" v-if="userInfo">
            {{ userInfo.is_vip ? 'VIP 用户' : '普通用户' }}
          </el-tag>
        </div>
      </div>
    </el-card>

    <!-- SearchBar -->
    <div class="search-section">
      <div class="search-container">
        <div class="search-inputs">
          <el-input 
            class="search-input"
            placeholder="搜索名字 (Uyghur / Chinese / Latin)" 
            prefix-icon="el-icon-search" 
            v-model="searchTip.keyword" 
            clearable
            @keyup.enter.native="handleSearch">
          </el-input>
          <el-select v-model="searchTip.gender" placeholder="性别" clearable class="search-input">
            <el-option label="全部" value=""></el-option>
            <el-option label="男" value="male"></el-option>
            <el-option label="女" value="female"></el-option>
            <el-option label="通用" value="unisex"></el-option>
          </el-select>
        </div>
        <div class="button-group">
          <el-button type="primary" @click="handleSearch" class="add-button">搜索</el-button>
          <el-button @click="resetSearch" class="add-button">重置</el-button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="table-container">
      <el-table 
        :data="nameList" 
        border 
        stripe
        :header-cell-class-name="'headerBg'"
        class="custom-table"
        style="width: 100%"
        v-loading="loading">
        <el-table-column type="expand" width="40">
          <template slot-scope="scope">
            <div class="expand-content">
              <div class="expand-item">
                <span class="expand-label">描述：</span>
                <span class="expand-value">{{ scope.row.name_desc|| '-' }}</span>
              </div>
              <div class="expand-item">
                <span class="expand-label">含义：</span>
                <span class="expand-value">{{ scope.row.name_meaning || '-' }}</span>
              </div>
              <div class="expand-item">
                <span class="expand-label">来源：</span>
                <span class="expand-value">{{ scope.row.origin || '-' }}</span>
              </div>
              <div class="expand-item">
                <span class="expand-label">标签：</span>
                <span class="expand-value">{{ scope.row.tag || '-' }}</span>
              </div>
              <div class="expand-item">
                <span class="expand-label">收藏时间：</span>
                <span class="expand-value">{{ scope.row.favorited_at || '-' }}</span>
              </div>
              <div class="expand-item">
                <span class="expand-label">会员可见：</span>
                <span class="expand-value">
                  <el-tag :type="scope.row.is_member_only ? 'warning' : 'info'" size="mini">
                    {{ scope.row.is_member_only ? '是' : '否' }}
                  </el-tag>
                </span>
              </div>
              <div class="expand-item">
                <span class="expand-label">隐藏状态：</span>
                <span class="expand-value">
                  <el-tag :type="scope.row.is_hidden ? 'danger' : 'success'" size="mini">
                    {{ scope.row.is_hidden ? '已隐藏' : '显示中' }}
                  </el-tag>
                </span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column type="index" label="#" width="50" align="center"></el-table-column>
        <el-table-column prop="name_uyghur" label="Uyghur" :align="'center'">
          <template slot-scope="scope">
            <div class="ugText">{{ scope.row.name_uyghur }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="name_chinese" label="Chinese" align="center"></el-table-column>
        <el-table-column prop="name_latin" label="Latin" align="center"></el-table-column>
        <el-table-column prop="gender" label="Gender" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="genderTagType(scope.row.gender)" disable-transitions>
              {{ scope.row.gender === 'male' ? '男' : scope.row.gender === 'female' ? '女' : scope.row.gender }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="origin" label="Origin" width="140" align="center"></el-table-column>
        <el-table-column prop="tag" label="Tag" width="140" align="center"></el-table-column>
        <el-table-column label="会员可见" width="120" align="center">
          <template slot-scope="scope">
            <el-tag 
              :type="scope.row.is_member_only ? 'warning' : 'info'" 
              disable-transitions
              size="small">
              <i v-if="scope.row.is_member_only" class="el-icon-lock"></i>
              <i v-else class="el-icon-unlock"></i>
              {{ scope.row.is_member_only ? '会员专享' : '普通可见' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="隐藏状态" width="120" align="center">
          <template slot-scope="scope">
            <el-tag 
              :type="scope.row.is_hidden ? 'danger' : 'success'" 
              disable-transitions
              size="small">
              <i v-if="scope.row.is_hidden" class="el-icon-remove"></i>
              <i v-else class="el-icon-view"></i>
              {{ scope.row.is_hidden ? '已隐藏' : '显示中' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="收藏时间" width="180" align="center">
          <template slot-scope="scope">
            {{ scope.row.favorited_at || '-' }}
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination -->
      <div class="pagination-wrapper">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageNum"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pageSize"
          :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
          :total="total">
        </el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDateTime } from '@/utils/timeUtil';

export default {
  name: 'UserFavorites',
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      nameList: [],
      total: 0,
      pageNum: 1,
      pageSize: 20,
      loading: false,
      searchTip: { 
        keyword: '', 
        gender: ''
      },
      isMobile: window.innerWidth <= 768,
      userId: null,
      userInfo: null
    }
  },
  created() {
    this.userId = this.id || this.$route.params.id
    if (!this.userId) {
      this.$message.error('缺少用户ID')
      this.goBack()
      return
    }
    this.fetchUserInfo()
    this.getFavorites()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 768
    },
    genderTagType(gender){
      if(!gender) return 'info'
      const g = String(gender).toLowerCase()
      if(g === 'male' || g === 'm') return 'primary'
      if(g === 'female' || g === 'f') return 'danger'
      return 'success'
    },
    async fetchUserInfo() {
      try {
        const res = await this.request.get(`/namzar/admin/miniUsers/${this.userId}`)
        const data = res.data || res
        this.userInfo = data
      } catch (res) {
        this.$message.error(res?.message || '获取用户信息失败')
      }
    },
    getFavorites(){
      this.loading = true
      // 构建查询参数，过滤空值
      const params = {
        pageNum: this.pageNum,
        pageSize: this.pageSize
      }
      if (this.searchTip.keyword) params.keyword = this.searchTip.keyword
      if (this.searchTip.gender) params.gender = this.searchTip.gender

      this.request.get(`/namzar/admin/miniUsers/${this.userId}/favorites`, { params }).then(res => {
        // 处理响应数据
        let data = res
        if (res.code === 200 && res.data) {
          data = res.data
        }
        
        const list = Array.isArray(data?.nameList) ? data.nameList : []
        this.total = data.total || list.length
        this.pageNum = data.page || this.pageNum
        this.pageSize = data.pageSize || this.pageSize
        
        this.nameList = list.map(item => ({
          id: item.id || item._id || item.nameId || item.name_id,
          name_uyghur: item.name_uyghur || '',
          name_chinese: item.name_chinese || '',
          name_latin: item.name_latin || '',
          name_meaning: item.name_meaning || '',
          name_desc: item.name_desc || '',
          tag: item.tag || '',
          origin: item.origin ||  '',
          gender: item.gender || '',
          is_member_only: item.is_member_only === 1 || item.is_member_only === true || false,
          is_hidden: item.is_hidden === 1 || item.is_hidden === true || false,
          favorited_at: item.favorited_at ? formatDateTime(item.favorited_at) : (item.favoritedAt ? formatDateTime(item.favoritedAt) : ''),
        }))
      }).catch(res => {
        this.$message.error(res?.message || '获取收藏列表失败')
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.pageNum = 1
      this.getFavorites()
    },
    resetSearch() {
      this.searchTip.keyword = ''
      this.searchTip.gender = ''
      this.pageNum = 1
      this.getFavorites()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.pageNum = 1
      this.getFavorites()
    },
    handleCurrentChange(page) {
      this.pageNum = page
      this.getFavorites()
    },
    goBack() {
      this.$router.push('/admin/mini-users')
    }
  },
}
</script>

<style scoped>
.user-favorites-container {
  padding: 30px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.header-card {
  margin-bottom: 20px;
  border-radius: 12px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.left-section {
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
}

.user-info {
  flex: 1;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.user-meta {
  margin: 8px 0 0;
  font-size: 14px;
  color: #909399;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.right-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-section {
  background: white;
  padding: 15px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.search-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.search-inputs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.search-input {
  width: 100%;
}

.button-group {
  display: flex;
  gap: 10px;
}

.add-button {
  width: 100%;
  margin-top: 10px;
}

.search-input ::v-deep .el-input__inner {
  height: 40px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  padding-left: 40px;
  font-size: 14px;
}

.search-input ::v-deep .el-input__prefix {
  left: 12px;
  color: #909399;
}

.table-container {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
}

.custom-table {
  border-radius: 8px;
  overflow: hidden;
  min-width: 1000px;
}

.custom-table ::v-deep .el-table__header-wrapper {
  background-color: #f5f7fa;
}

.custom-table ::v-deep .el-table__header th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

.custom-table ::v-deep .el-table__row:hover {
  background-color: #f5f7fa;
}

.headerBg {
  background: #eee!important;
}

.expand-content {
  padding: 15px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px 20px;
}

.expand-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
}

.expand-label {
  color: #606266;
  font-weight: 500;
  min-width: 110px;
}

.expand-value {
  color: #303133;
}

.ugText {
  direction: rtl;
}

.pagination-wrapper {
  margin-top: 20px;
  text-align: center;
}

.pagination-wrapper ::v-deep .el-pagination {
  justify-content: center;
}

/* Media Queries */
@media screen and (min-width: 768px) {
  .user-favorites-container {
    padding: 30px;
  }

  .search-container {
    flex-direction: row;
    align-items: center;
  }

  .search-inputs {
    flex-direction: row;
    flex: 1;
  }

  .button-group {
    flex-direction: row;
  }

  .add-button {
    width: auto;
    margin-top: 0;
  }
}
</style>

